import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type {
  ProvisioningRequest,
  ProvisionInput,
  StepHistoryEntry,
  AddressData,
} from "./types";
import { PROVISIONING_SELECTION_SET } from "./types";

const client = generateClient<Schema>();

const PAGE_SIZE = 50;

export interface ProvPage {
  items: ProvisioningRequest[];
  nextToken: string | null;
}

// ── Secondary-index cast helpers ──────────────────────────────────────────────
// Amplify Gen 2 generates listBy* methods from secondary indexes at runtime but
// does not expose them in the generated TypeScript types. Access via narrowly-
// scoped cast, mirroring the pattern in money_repository.ts.

type ListByResult = Promise<{
  data: unknown[];
  nextToken?: string | null;
  errors?: { message: string }[];
}>;

interface ListByOpts {
  sortDirection?: "ASC" | "DESC";
  limit?: number;
  nextToken?: string;
  selectionSet?: readonly string[];
}

function listProvisioningRequestByStatusAndCreated(
  key: { status: string },
  opts: ListByOpts,
): ListByResult {
  const model = client.models.ProvisioningRequest as unknown as {
    listProvisioningRequestByStatusAndCreated: (
      key: { status: string },
      opts: ListByOpts,
    ) => ListByResult;
  };
  return model.listProvisioningRequestByStatusAndCreated(key, opts);
}

function listProvisioningRequestByEmail(
  key: { email: string },
  opts: ListByOpts,
): ListByResult {
  const model = client.models.ProvisioningRequest as unknown as {
    listProvisioningRequestByEmail: (
      key: { email: string },
      opts: ListByOpts,
    ) => ListByResult;
  };
  return model.listProvisioningRequestByEmail(key, opts);
}

/**
 * Amplify Gen 2 returns `a.json()` fields as a raw JSON string, not the
 * parsed object — the generated TS type is `unknown`, but at runtime it's
 * whatever was stored. The handler writes `stepHistory: "[]"` and appends
 * entries as JSON, so consumers have to parse defensively.
 */
function parseStepHistory(raw: unknown): StepHistoryEntry[] {
  if (Array.isArray(raw)) return raw as StepHistoryEntry[];
  if (typeof raw === "string" && raw.length > 0) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as StepHistoryEntry[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function mapRecord(raw: unknown): ProvisioningRequest {
  const r = raw as Schema["ProvisioningRequest"]["type"];
  return {
    ...r,
    stepHistory: parseStepHistory(r.stepHistory),
    businessAddress: (r.businessAddress as AddressData) ?? {},
    individualAddress: (r.individualAddress as AddressData) ?? {},
    failedStep: r.failedStep ?? null,
    failedError: r.failedError ?? null,
    ssnLast4: r.ssnLast4 ?? undefined,
    taxId: r.taxId ?? undefined,
    businessUrl: r.businessUrl ?? undefined,
    supportPhone: r.supportPhone ?? undefined,
    cognitoUserId: r.cognitoUserId ?? undefined,
    userProfileId: r.userProfileId ?? undefined,
    businessProfileId: r.businessProfileId ?? undefined,
    stripeConnectedAccountId: r.stripeConnectedAccountId ?? undefined,
    stripeTerminalLocationId: r.stripeTerminalLocationId ?? undefined,
    launchpadApplicationId: r.launchpadApplicationId ?? undefined,
  } as ProvisioningRequest;
}

export class ProvisioningRepository {
  async list(filter?: { status?: string }): Promise<ProvisioningRequest[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify's `selectionSet` generic doesn't infer dotted nested paths cleanly; same shape as merchants_repository.
    const listOpts: any = {
      filter: filter?.status ? { status: { eq: filter.status } } : undefined,
      limit: 100,
      selectionSet: [...PROVISIONING_SELECTION_SET],
    };
    const { data, errors } =
      await client.models.ProvisioningRequest.list(listOpts);
    if (errors?.length) throw new Error(errors[0].message);
    return (data ?? []).map(mapRecord);
  }

  async listPage(opts?: {
    status?: string;
    nextToken?: string;
    limit?: number;
  }): Promise<ProvPage> {
    const limit = opts?.limit ?? PAGE_SIZE;
    if (opts?.status) {
      const { data, nextToken, errors } =
        await listProvisioningRequestByStatusAndCreated(
          { status: opts.status },
          {
            sortDirection: "DESC",
            limit,
            nextToken: opts.nextToken,
            selectionSet: [...PROVISIONING_SELECTION_SET],
          },
        );
      if (errors?.length) throw new Error(errors[0].message);
      return {
        items: (data ?? []).map(mapRecord),
        nextToken: nextToken ?? null,
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see list()
    const listOpts: any = {
      limit,
      nextToken: opts?.nextToken,
      selectionSet: [...PROVISIONING_SELECTION_SET],
    };
    const { data, nextToken, errors } =
      await client.models.ProvisioningRequest.list(listOpts);
    if (errors?.length) throw new Error(errors[0].message);
    return {
      items: (data ?? []).map(mapRecord),
      nextToken: nextToken ?? null,
    };
  }

  async findByEmail(email: string): Promise<ProvisioningRequest[]> {
    const { data, errors } = await listProvisioningRequestByEmail(
      { email: email.trim().toLowerCase() },
      { selectionSet: [...PROVISIONING_SELECTION_SET] },
    );
    if (errors?.length) throw new Error(errors[0].message);
    return (data ?? []).map(mapRecord);
  }

  async searchByBusinessName(
    query: string,
    nextToken?: string,
  ): Promise<ProvPage> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see list()
    const listOpts: any = {
      limit: PAGE_SIZE,
      nextToken,
      selectionSet: [...PROVISIONING_SELECTION_SET],
      filter: { businessName: { contains: query.trim() } },
    };
    const {
      data,
      nextToken: pageToken,
      errors,
    } = await client.models.ProvisioningRequest.list(listOpts);
    if (errors?.length) throw new Error(errors[0].message);
    return {
      items: (data ?? []).map(mapRecord),
      nextToken: pageToken ?? null,
    };
  }

  async get(id: string): Promise<ProvisioningRequest | null> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see list()
    const getOpts: any = { selectionSet: [...PROVISIONING_SELECTION_SET] };
    const { data, errors } = await client.models.ProvisioningRequest.get(
      { id },
      getOpts,
    );
    if (errors?.length) throw new Error(errors[0].message);
    return data ? mapRecord(data) : null;
  }

  async provision(input: ProvisionInput): Promise<{
    provisionId?: string | null;
    status?: string | null;
    failedStep?: string | null;
    failedError?: string | null;
    errors?: (string | null)[] | null;
  }> {
    const { data, errors } = await client.mutations.AdminProvision({
      action: "provision",
      input: {
        input: {
          ...input,
          businessAddress: input.businessAddress,
          individualAddress: input.individualAddress,
        },
      },
    });
    if (errors?.length) throw new Error(errors[0].message);
    return data ?? { errors: ["No response"] };
  }

  async retry(provisionId: string): Promise<{
    provisionId?: string | null;
    status?: string | null;
    failedStep?: string | null;
    failedError?: string | null;
    errors?: (string | null)[] | null;
  }> {
    const { data, errors } = await client.mutations.AdminProvision({
      action: "retry",
      input: { provisionId },
    });
    if (errors?.length) throw new Error(errors[0].message);
    return data ?? { errors: ["No response"] };
  }

  async update(
    provisionId: string,
    input: Partial<ProvisionInput>,
  ): Promise<{
    provisionId?: string | null;
    status?: string | null;
    failedStep?: string | null;
    errors?: (string | null)[] | null;
  }> {
    const { data, errors } = await client.mutations.AdminProvision({
      action: "update",
      input: {
        provisionId,
        input: input as unknown as NonNullable<
          NonNullable<
            Parameters<typeof client.mutations.AdminProvision>[0]["input"]
          >["input"]
        >,
      },
    });
    if (errors?.length) throw new Error(errors[0].message);
    return data ?? { errors: ["No response"] };
  }

  async cancel(provisionId: string): Promise<void> {
    const { errors } = await client.models.ProvisioningRequest.update({
      id: provisionId,
      status: "cancelled",
    });
    if (errors?.length) throw new Error(errors[0].message);
  }

  async resendWelcomeEmail(provisionId: string): Promise<{
    success?: boolean | null;
    errors?: (string | null)[] | null;
  }> {
    const { data, errors } = await client.mutations.AdminProvision({
      action: "resendWelcomeEmail",
      input: { provisionId },
    });
    if (errors?.length) throw new Error(errors[0].message);
    return data ?? { errors: ["No response"] };
  }
}
